/**
 * Production-ready logging utility
 * Replaces console.log statements with proper logging levels and environment-aware output
 */

// Log levels
const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
};

class Logger {
  constructor() {
    this.environment = process.env.REACT_APP_ENVIRONMENT || 'development';
    this.logLevel = this.getLogLevel();
    this.isProduction = this.environment === 'production';
    this.disableConsoleInProduction = process.env.REACT_APP_DISABLE_CONSOLE_IN_PRODUCTION === 'true';
  }

  getLogLevel() {
    const level = process.env.REACT_APP_LOG_LEVEL || 'info';
    return LOG_LEVELS[level.toUpperCase()] ?? LOG_LEVELS.INFO;
  }

  shouldLog(level) {
    return level <= this.logLevel && !(this.isProduction && this.disableConsoleInProduction);
  }

  formatMessage(level, message, ...args) {
    const timestamp = new Date().toISOString();
    const levelName = Object.keys(LOG_LEVELS)[level];
    return {
      timestamp,
      level: levelName,
      message,
      args,
      environment: this.environment
    };
  }

  log(level, message, ...args) {
    if (!this.shouldLog(level)) return;

    const formatted = this.formatMessage(level, message, ...args);

    // In production, we might want to send logs to a service
    if (this.isProduction) {
      this.sendToLoggingService(formatted);
    } else {
      // Development logging
      const consoleMethod = level === LOG_LEVELS.ERROR ? 'error' :
                           level === LOG_LEVELS.WARN ? 'warn' : 'log';

      console[consoleMethod](`[${formatted.timestamp}] [${formatted.level}] ${message}`, ...args);
    }
  }

  error(message, ...args) {
    this.log(LOG_LEVELS.ERROR, message, ...args);
  }

  warn(message, ...args) {
    this.log(LOG_LEVELS.WARN, message, ...args);
  }

  info(message, ...args) {
    this.log(LOG_LEVELS.INFO, message, ...args);
  }

  debug(message, ...args) {
    this.log(LOG_LEVELS.DEBUG, message, ...args);
  }

  // Performance logging
  time(label) {
    if (this.shouldLog(LOG_LEVELS.DEBUG)) {
      console.time(label);
    }
  }

  timeEnd(label) {
    if (this.shouldLog(LOG_LEVELS.DEBUG)) {
      console.timeEnd(label);
    }
  }

  // Future: Send logs to external service in production
  sendToLoggingService(logData) {
    // TODO: Integrate with logging service (Sentry, LogRocket, etc.)
    // For now, store critical errors in localStorage for debugging
    if (logData.level === 'ERROR') {
      try {
        const errors = JSON.parse(localStorage.getItem('repconnect_errors') || '[]');
        errors.push({ ...logData, timestamp: Date.now() });
        // Keep only last 10 errors
        if (errors.length > 10) errors.splice(0, errors.length - 10);
        localStorage.setItem('repconnect_errors', JSON.stringify(errors));
      } catch (e) {
        // Fail silently if localStorage is unavailable
      }
    }
  }

  // Get stored errors for debugging
  getStoredErrors() {
    try {
      return JSON.parse(localStorage.getItem('repconnect_errors') || '[]');
    } catch (e) {
      return [];
    }
  }

  // Clear stored errors
  clearStoredErrors() {
    try {
      localStorage.removeItem('repconnect_errors');
    } catch (e) {
      // Fail silently
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export for testing
export { Logger, LOG_LEVELS };